import type { CalendarEvent } from "@/planning/model/wire.js";
import { isoDayInZone } from "@/platform/kit";
import { addDays, isoDateKey } from "@/platform/client";

const MAX_SPAN_DAYS = 90;

/** A `YYYY-MM-DD` key → the civil Date at local midnight, for span iteration. */
function civilDate(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

/** The home-zone day an event starts on, as the civil Date the grid uses. */
export function eventStartDay(ev: CalendarEvent, tz: string): Date {
  return civilDate(eventCoveredDayKeys(ev, tz)[0]!);
}

/**
 * The home-zone day keys an event covers — like Google Calendar, a multi-day
 * event appears on every day it spans. Timed events use an exclusive end (an
 * event ending at 00:00 doesn't bleed into the next day); all-day events are
 * inclusive.
 *
 * The two kinds are bucketed differently on purpose (docs/timezones.md):
 *   • ALL-DAY events are zone-less. They are stored as `…T00:00:00.000Z`, and
 *     their civil day is the stored UTC Y-M-D read VERBATIM — never converted
 *     through a zone, which would shift them a day in a browser east or west
 *     of UTC.
 *   • TIMED events are instants, so their day is that instant resolved in the
 *     home zone `tz`.
 */
export function eventCoveredDayKeys(ev: CalendarEvent, tz: string): string[] {
  const startKey = ev.allDay ? ev.start.slice(0, 10) : isoDayInZone(new Date(ev.start), tz);
  let endKey = startKey;
  if (ev.end) {
    if (ev.allDay) {
      endKey = ev.end.slice(0, 10);
    } else {
      const startMs = Date.parse(ev.start);
      const endMs = Date.parse(ev.end);
      endKey = endMs - 1 <= startMs ? startKey : isoDayInZone(new Date(endMs - 1), tz);
    }
  }
  const keys: string[] = [];
  let d = civilDate(startKey);
  const end = civilDate(endKey);
  for (let i = 0; i < MAX_SPAN_DAYS && d <= end; i++) {
    keys.push(isoDateKey(d));
    d = addDays(d, 1);
  }
  return keys;
}

/** Map of home-zone day key -> events covering that day (sorted: all-day first, then time). */
export function buildDayEventMap(
  events: CalendarEvent[],
  tz: string,
): Map<string, CalendarEvent[]> {
  const m = new Map<string, CalendarEvent[]>();
  for (const ev of events) {
    for (const key of eventCoveredDayKeys(ev, tz)) {
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(ev);
    }
  }
  for (const list of m.values()) {
    list.sort((a, b) => {
      if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
      return a.start.localeCompare(b.start);
    });
  }
  return m;
}
